import React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLogin } from '../hooks/useLogin';
import { UserContext } from '../context/UserContext';
import { AppRoutes } from '../routes';
import { useFeatureFlagNavigation } from '../hooks/useFeatureFlagNavigation';

const Navbar: React.FC = () => {
  const authService = useAuth();
  const navigate = useNavigate();
  const { navigateTo } = useFeatureFlagNavigation();
  const { logout } = useLogin();
  const { userType } = React.useContext(UserContext);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const isLoggedIn = authService.getCurrentUser();

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            YoTutor
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {isLoggedIn ? (
                <MenuItem onClick={() => navigate(AppRoutes.DASHBOARD)}>
                  <Typography textAlign="center">Dashboard</Typography>
                </MenuItem>
              ) : (
                <>
                  <MenuItem onClick={() => navigate(AppRoutes.HOME)}>
                    <Typography textAlign="center">Find Tutors</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => navigate(AppRoutes.BECOME_TUTOR)}>
                    <Typography textAlign="center">Become a Tutor</Typography>
                  </MenuItem>
                </>
              )}
            </Menu>
          </Box>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}
          >
            YoTutor
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {isLoggedIn ? (
              <Button
                onClick={() => navigate(AppRoutes.DASHBOARD)}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate(AppRoutes.HOME)}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  Find Tutors
                </Button>
                <Button
                  onClick={() => navigate(AppRoutes.BECOME_TUTOR)}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  Become a Tutor
                </Button>
              </>
            )}
          </Box>

          {isLoggedIn ? (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt="User Avatar" src="/static/images/avatar/2.jpg" />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={() => {
                  if (userType === 'student') {
                    navigate(AppRoutes.STUDENT_PROFILE);
                  } else {
                    navigate(AppRoutes.TUTOR_PROFILE);
                  }
                }}>
                  <Typography textAlign="center">Profile</Typography>
                </MenuItem>
                <MenuItem onClick={() => logout()}>
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                onClick={() => navigateTo(AppRoutes.LOGIN)}
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Login
              </Button>
              <Button
                onClick={() => navigateTo(AppRoutes.SIGNUP)}
                variant="contained"
                color="secondary"
                sx={{ 
                  fontWeight: 'bold',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
